import React, { useCallback, useEffect, useState } from "react";
import {
  Card,
  Collapse,
  Flex,
  List,
  Modal,
  Skeleton,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { axiosInstance } from "@/utilities";
import { API_URL_PIM_PRODUCTS } from "@/services/XCONS_ENV";
import type { ProductState, ProductStateView, TransitionOption } from "./types";
import type { AccionDeEstado } from "./ProductStateSteps";

const { Text, Paragraph } = Typography;

/**
 * El texto de la acción depende del par (desde, hacia), no sólo del destino: `→ active`
 * es "Publicar" la primera vez y "Reactivar" cuando la URL ya existe, y `→ inactive`
 * es "Pausar" desde publicado y "Desarchivar" desde archivado. La matriz vive en el
 * backend; esto es sólo cómo se llama cada arista en la UI.
 */
const etiquetaAccion = (
  desde: ProductState,
  hacia: ProductState,
  neverPublished: boolean,
): string => {
  if (hacia === "review") return "Enviar a revisión";
  if (hacia === "draft") return "Volver a edición";
  if (hacia === "active") return neverPublished ? "Publicar" : "Reactivar";
  if (hacia === "inactive") {
    return desde === "archived" ? "Desarchivar" : "Pausar";
  }
  return "Archivar";
};

/** Las que piden confirmación: sacan el producto de la venta. */
const DESTRUCTIVAS: ProductState[] = ["inactive", "archived"];

/**
 * El estado del producto y sus transiciones, para el flujo de la cabecera y para la barra
 * de validación (xcons-docs#404). Una sola lectura de `GET /state` alimenta a los dos: el
 * flujo muestra el estado y ofrece las transiciones en su menú ▾; la barra, lo que falta.
 *
 * **Los permisos no se resuelven acá.** `GET /pim/products/:id/state` devuelve cada
 * transición con `allowed` y, si no, con el motivo: reimplementar la matriz, los dos
 * niveles del producto y el permiso global de catálogo en el front garantizaría dos
 * versiones de la misma regla el día que cambie.
 *
 * Las acciones que no se pueden **se muestran deshabilitadas y explicadas, no
 * escondidas** (R15): un Editor tiene que poder ver que existe "Publicar" y por qué no
 * le corresponde a él.
 */
export const useEstadoDelProducto = (
  productId: number | undefined,
  /** Se avisa al padre cuando el estado cambió, para que recargue el detalle. */
  onChanged?: () => void,
) => {
  const [data, setData] = useState<ProductStateView | null>(null);
  const [loading, setLoading] = useState(true);
  const [ejecutando, setEjecutando] = useState<ProductState | null>(null);
  const [sinEstado, setSinEstado] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    setSinEstado(null);
    try {
      const { data: res } = await axiosInstance.get<ProductStateView>(
        `${API_URL_PIM_PRODUCTS}/${productId}/state`,
      );
      setData(res);
    } catch (e) {
      const res = (
        e as { response?: { status?: number; data?: { message?: string } } }
      )?.response;
      const msg = res?.data?.message ?? "";

      // El 404 acá **no** significa "no existe el producto": significa que no tiene
      // `vv_state`, o sea que falta correr la migración de la Fase 9 en este ambiente.
      // Va como aviso en la barra y no como toast rojo, porque no es un error del
      // operador y no se arregla reintentando.
      if (res?.status === 404 && /vv_state/.test(msg)) {
        setSinEstado(msg);
      } else {
        message.error(msg || "No se pudo leer el estado del producto");
      }
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const ejecutar = async (to: ProductState) => {
    setEjecutando(to);
    try {
      const { data: res } = await axiosInstance.post(
        `${API_URL_PIM_PRODUCTS}/${productId}/transition`,
        { to },
      );
      message.success(
        res?.urlKey && to === "active"
          ? `Publicado en /${res.urlKey}`
          : "Estado actualizado",
      );
      // Los preexistentes vuelven como warnings del propio POST: se avisan, porque son
      // campos que quedaron sin completar en algo que ya está en la calle.
      if (Array.isArray(res?.warnings) && res.warnings.length) {
        message.warning(
          `Publicado con ${res.warnings.length} campo(s) de CON-P001 sin completar`,
        );
      }
      await refresh();
      onChanged?.();
    } catch (e) {
      const res = (
        e as {
          response?: {
            data?: { message?: string; findings?: Array<{ message: string }> };
          };
        }
      )?.response;
      const findings = res?.data?.findings;
      if (findings?.length) {
        Modal.error({
          title: "El producto no cumple CON-P001",
          content: (
            <List
              size="small"
              dataSource={findings}
              renderItem={(f) => <List.Item>{f.message}</List.Item>}
            />
          ),
          width: 520,
        });
      } else {
        message.error(res?.data?.message ?? "No se pudo cambiar el estado");
      }
    } finally {
      setEjecutando(null);
    }
  };

  const confirmar = (transicion: TransitionOption) => {
    if (!DESTRUCTIVAS.includes(transicion.to)) {
      ejecutar(transicion.to);
      return;
    }

    // Una sola pregunta y el botón primario, verde, como las acciones en lote
    // (xcons-docs#479). El verbo es el de la opción del menú: ir a Pausado desde Archivado
    // es Desarchivar.
    const verbo = etiquetaAccion(
      data!.state,
      transicion.to,
      data!.neverPublished,
    ).toLowerCase();
    Modal.confirm({
      title: `¿Estás seguro que querés ${verbo} este producto?`,
      okText: `Sí, ${verbo}`,
      cancelText: "Cancelar",
      icon: <ExclamationCircleOutlined />,
      onOk: () => ejecutar(transicion.to),
    });
  };

  /** Las transiciones como acciones del menú ▾, con el motivo que mandó el backend. */
  const acciones: AccionDeEstado[] = (data?.transitions ?? []).map((t) => ({
    key: t.to,
    label: etiquetaAccion(data!.state, t.to, data!.neverPublished),
    onClick: () => confirmar(t),
    disabled: !t.allowed || ejecutando !== null,
    motivo: t.allowed ? undefined : t.blockedReason,
  }));

  return { data, loading, sinEstado, acciones };
};

export type EstadoDelProducto = ReturnType<typeof useEstadoDelProducto>;

interface Props {
  estado: EstadoDelProducto;
}

/**
 * Lo que queda de la barra de estado: los avisos de nivel y el panel de CON-P001. El
 * estado y las transiciones se mudaron al flujo de la cabecera (xcons-docs#404).
 */
export const ProductStateBar: React.FC<Props> = ({ estado }) => {
  const { data, loading, sinEstado } = estado;

  if (loading && !data) {
    return (
      <Card>
        <Skeleton active paragraph={{ rows: 1 }} />
      </Card>
    );
  }

  // Sin banner cuando falta el estado (xcons-docs#479): el flujo no se dibuja y no hay qué
  // operar.
  if (sinEstado) return null;

  if (!data) return null;

  const { blocking, preexisting, warnings } = data.validation;
  // **El panel sale sólo con bloqueantes** (xcons-docs#432): es lo que frena la
  // publicación y el operador tiene que saber qué corregir. Los avisos y lo que ya venía
  // sin completar no frenan nada, y solos eran ruido en cada ficha.
  const hayPanel = blocking.length > 0;

  // Sin bloqueantes, la tarjeta no tiene qué decir: antes quedaba la URL y "Cumple
  // CON-P001", y ninguna de las dos le sirve al operador. **Nadie lleva aviso por su
  // nivel** —ni quien sólo mira (xcons-docs#466) ni el Editor (xcons-docs#479)—: las
  // acciones que no puede hacer ya se ven deshabilitadas.
  if (!hayPanel) return null;

  return (
    <Card size="small">
      <Flex vertical gap={12}>
        {hayPanel ? (
          <Collapse
            size="small"
            items={[
              {
                key: "validacion",
                label: (
                  <Space>
                    <Text>CON-P001</Text>
                    {blocking.length ? (
                      <Tag color="red">{blocking.length} bloqueante(s)</Tag>
                    ) : null}
                    {preexisting.length ? (
                      <Tag color="orange">
                        {preexisting.length} sin completar
                      </Tag>
                    ) : null}
                    {warnings.length ? (
                      <Tag>{warnings.length} aviso(s)</Tag>
                    ) : null}
                  </Space>
                ),
                children: (
                  <Flex vertical gap={12}>
                    {blocking.length > 0 && (
                      <div>
                        <Text strong type="danger">
                          Falta para publicar
                        </Text>
                        <List
                          size="small"
                          dataSource={blocking}
                          renderItem={(f) => (
                            <List.Item>
                              <Space size={4}>
                                <Tag>{f.field}</Tag>
                                <Text>{f.message}</Text>
                              </Space>
                            </List.Item>
                          )}
                        />
                      </div>
                    )}

                    {preexisting.length > 0 && (
                      <div>
                        <Text strong>Sin completar, pero ya publicado</Text>
                        <Paragraph
                          type="secondary"
                          style={{ fontSize: 12, marginBottom: 4 }}
                        >
                          No frenan publicar: el producto ya está en la calle
                          así. Se piden para que se completen, no para bloquear
                          una corrección de texto.
                        </Paragraph>
                        <List
                          size="small"
                          dataSource={preexisting}
                          renderItem={(f) => (
                            <List.Item>
                              <Space size={4}>
                                <Tag>{f.field}</Tag>
                                <Text type="secondary">{f.message}</Text>
                              </Space>
                            </List.Item>
                          )}
                        />
                      </div>
                    )}

                    {warnings.length > 0 && (
                      <div>
                        <Text strong>Avisos</Text>
                        <List
                          size="small"
                          dataSource={warnings}
                          renderItem={(f) => (
                            <List.Item>
                              <Space size={4}>
                                <Tag>{f.field}</Tag>
                                <Text type="secondary">{f.message}</Text>
                              </Space>
                            </List.Item>
                          )}
                        />
                      </div>
                    )}
                  </Flex>
                ),
              },
            ]}
          />
        ) : null}
      </Flex>
    </Card>
  );
};
